import { Project } from '@/app/api';
import { PaymentMethod } from '@/app/enums';
import { pushAdditionalGaIds } from '@/app/hooks/useAnalytics/push-additional-ga-ids';
import { pushToDataLayer } from '@/app/hooks/useAnalytics/push-to-data-layer';
import {
  CheckoutSteps,
  CheckoutTypes,
  getProjectBrand,
  getProjectCategory,
  GoogleTagManagerEventNames,
  ItemCategory2,
  MixpanelEvent,
  PageTypes,
} from '@/app/hooks/useAnalytics/types';
import { envs } from '@/app/utils/envs';
import { getAmountInRespectiveCurrency } from '@/app/utils/numbers';

import { mixpanelTrack } from '@/app/hooks/useAnalytics/mixpanel-track';
import { CreateDonationRequest } from '@/src/api/models';

export const getMixpanelDonationCompletedEvent = ({
  project,
  donationId,
  paymentMethod,
  requestDto,
  abGroup,
}: PushDonationCompletedEventParams) => {
  if (!envs.isMixpanelEnabled) {
    if (envs.isAnalyticInDebugMode) {
      console.log('Skipping Mixpanel - DonationCompletedEvent');
    }
    return;
  }
  return {
    page_type: PageTypes.Checkout,
    checkout_type: requestDto.goodHabitCategory ? CheckoutTypes.Subscription : CheckoutTypes.OneTime,
    step_name: CheckoutSteps.DonationForm,
    project_id: project.numericId,
    project_name: project.content.baseName,
    project_mode: project.settings.modeGroup,
    project_main_category: project.settings.taxonomy.mainCategory?.name,
    project_categories: project.settings.taxonomy.categories?.map((category) => category.name),
    topic: project.settings.taxonomy.topic?.name,
    donation_id: donationId,
    donation_mode: requestDto.mode,
    amount: getAmountInRespectiveCurrency(requestDto.amountInCents),
    tip: getAmountInRespectiveCurrency(requestDto.tipInCents),
    currency: requestDto.currency,
    payment_method: paymentMethod,
    donation_receipt: requestDto.donationReceipt,
    abGroup,
    // TODO Tady je pro to trochu složitější mechanika, která se checkuje oproti DB, takže to buď budeme taky vracet v response a nebo zkusím maťa, jestli to nemůžeme vyhodit
    // Tady to funguje tak, že když už jednou přispěl se stejným e-mailem, tak tam pořád posíláme true, i když u tohohle příspěvku zaškrtnul, že nic nechce, protože tam je pořád souhlas z předchozího příspěvku
    newsletter_optout: requestDto.newsletterOptOut,
    $insert_id: donationId,
    // TODO mixpanel.identify workaround
    $user_id: requestDto.user,
  };
};

export const publishGtmDonationCompletedEvent = ({
  project,
  abGroup,
  requestDto,
}: PushDonationCompletedEventParams) => {
  if (!envs.isGtmEnabled) {
    if (envs.isAnalyticInDebugMode) {
      console.log('Skipping Google Tag Manager - DonationCompletedEvent');
    }
    return;
  }
  const itemBase = {
    item_id: project.numericId,
    item_brand: getProjectBrand(project),
    item_category: getProjectCategory(project),
    item_category_2: project.settings.isLongTerm ? ItemCategory2.LongTerm : ItemCategory2.Single,
    quantity: 1,
    abGroup,
  };

  const items = [
    {
      ...itemBase,
      item_name: project.content.baseName,
      item_price: getAmountInRespectiveCurrency(requestDto.amountInCents),
    },
  ];

  if (requestDto.tipInCents) {
    items.push({
      ...itemBase,
      item_name: 'Příspěvek provozovateli',
      item_price: getAmountInRespectiveCurrency(requestDto.tipInCents),
    });
  }

  const gtmObject = {
    ecommerce: {
      items,
    },
  };

  if (envs.isAnalyticInDebugMode) {
    console.log('Google Tag Manager - DonationCompletedEvent', gtmObject);
  }

  pushAdditionalGaIds(GoogleTagManagerEventNames.Purchase, project);
  pushToDataLayer(gtmObject);
};

export type PushDonationCompletedEventParams = {
  donationId: string;
  requestDto: CreateDonationRequest;
  project: Project;
  paymentMethod: PaymentMethod;
  areStatisticsCookiesAllowed: boolean;
  abGroup: string;
};

export type PushDonationCompletedEventBaseParams = Omit<
  PushDonationCompletedEventParams,
  'areStatisticsCookiesAllowed' | 'abGroup'
>;

export const pushDonationCompletedEvent = ({
  requestDto,
  project,
  paymentMethod,
  donationId,
  areStatisticsCookiesAllowed,
  abGroup,
}: PushDonationCompletedEventParams) => {
  if (!areStatisticsCookiesAllowed) {
    if (envs.isAnalyticInDebugMode) {
      console.log('Skiping DonationCompletedEvent, because statistics cookies are not allowed');
    }
    return;
  }

  mixpanelTrack(MixpanelEvent.DonationCompleted, {
    page_type: PageTypes.Checkout,
    checkout_type: requestDto.goodHabitCategory ? CheckoutTypes.Subscription : CheckoutTypes.OneTime,
    step_name: CheckoutSteps.DonationForm,
    project_id: project.numericId,
    project_name: project.content.baseName,
    project_mode: project.settings.modeGroup,
    project_main_category: project.settings.taxonomy.mainCategory?.name,
    project_categories: project.settings.taxonomy.categories?.map((category) => category.name),
    topic: project.settings.taxonomy.topic?.name,
    donation_id: donationId,
    donation_mode: requestDto.mode,
    amount: getAmountInRespectiveCurrency(requestDto.amountInCents),
    tip: getAmountInRespectiveCurrency(requestDto.tipInCents),
    currency: requestDto.currency,
    payment_method: paymentMethod,
    donation_receipt: requestDto.donationReceipt,
    abGroup,
    // TODO Tady je pro to trochu složitější mechanika, která se checkuje oproti DB, takže to buď budeme taky vracet v response a nebo zkusím maťa, jestli to nemůžeme vyhodit
    // Tady to funguje tak, že když už jednou přispěl se stejným e-mailem, tak tam pořád posíláme true, i když u tohohle příspěvku zaškrtnul, že nic nechce, protože tam je pořád souhlas z předchozího příspěvku
    newsletter_optout: requestDto.newsletterOptOut,
    $insert_id: donationId,
    // TODO mixpanel.identify workaround
    $user_id: requestDto.user,
  });

  publishGtmDonationCompletedEvent({
    abGroup,
    areStatisticsCookiesAllowed,
    donationId,
    paymentMethod,
    project,
    requestDto,
  });
};
